/* eslint-disable no-magic-numbers */
import { SOCIAL_AD_TYPES } from "utils/constants";
import { GIGA, MEGA, SECONDS_PER_MIN } from "utils/common";

import { isURL, maxLength, requiredNotNull } from "widgets/forms/validators/validators";
import { creativeNameAllowedChars, creativeNameMaxLength } from "widgets/forms/validators/Creatives";
import { filesize, duration, exactDimensions } from "widgets/forms/validators/Assets";
import {
	conditionalRequiredNotNull,
	errorMessageReducer,
	makeDynamicConfig,
	minCards,
	requiredAsset,
	cardValidator,
	requiredWithDynamicImageType,
	requiredWithDynamicVideoType,
	validateUnlessExternalRender,
	requiredImposiumComp,
	requiredImposiumProject,
	requiredAfterEffectsPackage,
	requiredAfterEffectsComp
} from "./validators";
import { mergeLists, uniqueVariableName } from "social/validators/utils";
import { imageMaxBytes, videoMaxBytes } from "social/utils";


/**
 * Snapchat Image
 ********************************************/

export const SNAPCHAT_COMMON_CONSTRAINTS = {
	MAX_DISCLAIMER_NAME_LENGTH: 255,
	MAX_DISCLAIMER_TEXT_LENGTH: 5000,
};

export const SNAPCHAT_IMAGE_CONSTRAINTS = {
	MAX_LENGTH_HEADLINE: 34,
	MAX_LENGTH_BRAND_NAME: 25,
	MAX_LENGTH_LANDING_PAGE: 800,
	MAX_IMAGE_BYTES: imageMaxBytes(5 * MEGA),
	IMAGE_ACCEPT: ".jpg, .jpeg, .png",
	DIMENSIONS: {
		width: 1080,
		height: 1920,
	},
};

export const SNAPCHAT_IMAGE_VALIDATORS = {
	imposiumProject: [
		requiredImposiumProject
	],
	imposiumComposition: [
		requiredImposiumComp,
	],
	image: validateUnlessExternalRender([
		requiredAsset,
		filesize({
			max: SNAPCHAT_IMAGE_CONSTRAINTS.MAX_IMAGE_BYTES,
		}),
		exactDimensions(SNAPCHAT_IMAGE_CONSTRAINTS.DIMENSIONS),
	]),
	headline: [
		requiredNotNull,
		maxLength(SNAPCHAT_IMAGE_CONSTRAINTS.MAX_LENGTH_HEADLINE),
	],
	brandName: [
		maxLength(SNAPCHAT_IMAGE_CONSTRAINTS.MAX_LENGTH_BRAND_NAME),
	],
	landingPage: [
		requiredNotNull,
		isURL,
		maxLength(SNAPCHAT_IMAGE_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE),
	],
	cta: [
		requiredNotNull
	],
	disclaimerName: [
		conditionalRequiredNotNull("disclaimerEnabled"),
		maxLength(SNAPCHAT_COMMON_CONSTRAINTS.MAX_DISCLAIMER_NAME_LENGTH),
	],
	disclaimerText: [
		conditionalRequiredNotNull("disclaimerEnabled"),
		maxLength(SNAPCHAT_COMMON_CONSTRAINTS.MAX_DISCLAIMER_TEXT_LENGTH),
	],
};

const SNAPCHAT_IMAGE_WARNINGS = mergeLists(
	{
		image: [uniqueVariableName],
		headline: [uniqueVariableName],
		brandName: [uniqueVariableName],
		landingPage: [uniqueVariableName],
		cta: [uniqueVariableName],
	},
	makeDynamicConfig(SNAPCHAT_IMAGE_VALIDATORS)
);

export const snapchatImageWarning = makeSnapchatWarning(SOCIAL_AD_TYPES.SNAPCHAT_IMAGE, SNAPCHAT_IMAGE_WARNINGS);
export const snapchatImageValidator = makeSnapchatValidator(SOCIAL_AD_TYPES.SNAPCHAT_IMAGE, SNAPCHAT_IMAGE_WARNINGS);

/**
 * Snapchat Video
 ********************************************/

export const SNAPCHAT_VIDEO_CONSTRAINTS = {
	MAX_LENGTH_HEADLINE: 34,
	MAX_LENGTH_BRAND_NAME: 25,
	MAX_LENGTH_LANDING_PAGE: 800,
	MIN_VIDEO_SEC: 3,
	MAX_VIDEO_MIN: 3,
	MAX_VIDEO_BYTES: videoMaxBytes(1 * GIGA),
	VIDEO_ACCEPT: ".mp4, .mov",
	DIMENSIONS: {
		width: 1080,
		height: 1920,
	},
};

export const SNAPCHAT_VIDEO_VALIDATORS = {
	imposiumProject: [
		requiredImposiumProject
	],
	imposiumComposition: [
		requiredImposiumComp,
	],
	afterEffectsPackage: [
		requiredAfterEffectsPackage,
	],
	afterEffectsComposition: [
		requiredAfterEffectsComp,
	],
	video: validateUnlessExternalRender([
		requiredAsset,
		filesize({
			max: SNAPCHAT_VIDEO_CONSTRAINTS.MAX_VIDEO_BYTES,
		}),
		duration({
			min: SNAPCHAT_VIDEO_CONSTRAINTS.MIN_VIDEO_SEC,
			max: SNAPCHAT_VIDEO_CONSTRAINTS.MAX_VIDEO_MIN * SECONDS_PER_MIN,
		}),
		exactDimensions(SNAPCHAT_VIDEO_CONSTRAINTS.DIMENSIONS),
	]),
	headline: [
		requiredNotNull,
		maxLength(SNAPCHAT_VIDEO_CONSTRAINTS.MAX_LENGTH_HEADLINE),
	],
	brandName: [
		maxLength(SNAPCHAT_VIDEO_CONSTRAINTS.MAX_LENGTH_BRAND_NAME),
	],
	landingPage: [
		requiredNotNull,
		isURL,
		maxLength(SNAPCHAT_VIDEO_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE),
	],
	cta: [
		requiredNotNull,
	],
	disclaimerName: [
		conditionalRequiredNotNull("disclaimerEnabled"),
		maxLength(SNAPCHAT_COMMON_CONSTRAINTS.MAX_DISCLAIMER_NAME_LENGTH),
	],
	disclaimerText: [
		conditionalRequiredNotNull("disclaimerEnabled"),
		maxLength(SNAPCHAT_COMMON_CONSTRAINTS.MAX_DISCLAIMER_TEXT_LENGTH),
	],
};

const SNAPCHAT_VIDEO_WARNINGS = mergeLists(
	{
		video: [uniqueVariableName],
		headline: [uniqueVariableName],
		brandName: [uniqueVariableName],
		landingPage: [uniqueVariableName],
		cta: [uniqueVariableName],
	},
	makeDynamicConfig(SNAPCHAT_VIDEO_VALIDATORS)
);

export const snapchatVideoWarning = makeSnapchatWarning(SOCIAL_AD_TYPES.SNAPCHAT_VIDEO, SNAPCHAT_VIDEO_WARNINGS);
export const snapchatVideoValidator = makeSnapchatValidator(SOCIAL_AD_TYPES.SNAPCHAT_VIDEO, SNAPCHAT_VIDEO_WARNINGS);



/**
 * Snapchat Commercial
 ********************************************/

export const SNAPCHAT_COMMERCIAL_CONSTRAINTS = {
	MAX_LENGTH_HEADLINE: 34,
	MAX_LENGTH_BRAND_NAME: 25,
	MAX_LENGTH_LANDING_PAGE: 800,
	MIN_VIDEO_SEC: 3,
	MAX_VIDEO_MIN: 3,
	MAX_VIDEO_BYTES: videoMaxBytes(1 * GIGA),
	VIDEO_ACCEPT: ".mp4, .mov",
	DIMENSIONS: {
		width: 1080,
		height: 1920,
	},
};

export const SNAPCHAT_COMMERCIAL_VALIDATORS = {
	imposiumProject: [
		requiredImposiumProject
	],
	imposiumComposition: [
		requiredImposiumComp,
	],
	afterEffectsPackage: [
		requiredAfterEffectsPackage,
	],
	afterEffectsComposition: [
		requiredAfterEffectsComp,
	],
	video: validateUnlessExternalRender([
		requiredAsset,
		filesize({
			max: SNAPCHAT_COMMERCIAL_CONSTRAINTS.MAX_VIDEO_BYTES,
		}),
		duration({
			min: SNAPCHAT_COMMERCIAL_CONSTRAINTS.MIN_VIDEO_SEC,
			max: SNAPCHAT_COMMERCIAL_CONSTRAINTS.MAX_VIDEO_MIN * SECONDS_PER_MIN,
		}),
		exactDimensions(SNAPCHAT_COMMERCIAL_CONSTRAINTS.DIMENSIONS),
	]),
	headline: [
		requiredNotNull,
		maxLength(SNAPCHAT_COMMERCIAL_CONSTRAINTS.MAX_LENGTH_HEADLINE),
	],
	brandName: [
		maxLength(SNAPCHAT_COMMERCIAL_CONSTRAINTS.MAX_LENGTH_BRAND_NAME),
	],
	landingPage: [
		isURL,
		maxLength(SNAPCHAT_COMMERCIAL_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE),
	],
	disclaimerName: [
		conditionalRequiredNotNull("disclaimerEnabled"),
		maxLength(SNAPCHAT_COMMON_CONSTRAINTS.MAX_DISCLAIMER_NAME_LENGTH),
	],
	disclaimerText: [
		conditionalRequiredNotNull("disclaimerEnabled"),
		maxLength(SNAPCHAT_COMMON_CONSTRAINTS.MAX_DISCLAIMER_TEXT_LENGTH),
	],
};

const SNAPCHAT_COMMERCIAL_WARNINGS = mergeLists(
	{
		video: [uniqueVariableName],
		headline: [uniqueVariableName],
		brandName: [uniqueVariableName],
		landingPage: [uniqueVariableName],
		cta: [uniqueVariableName],
	},
	makeDynamicConfig(SNAPCHAT_COMMERCIAL_VALIDATORS)
);

function makeSnapchatWarning(formName, warnings) {
	return function(allValues) {
		const values = allValues[formName] || {};
		if (values.isDynamic) {
			return undefined;
		}

		return errorMessageReducer(warnings, values, formName);
	};
}

function makeSnapchatValidator(formName, warnings) {
	return function(allValues) {
		const values = allValues[formName] || {};
		let validators = { name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars]};
		if (values.isDynamic) {
			validators = { ...validators, ...warnings };
		}
		return errorMessageReducer(validators, values, formName);
	};
}

export const snapchatCommercialWarning = makeSnapchatWarning(SOCIAL_AD_TYPES.SNAPCHAT_COMMERCIAL, SNAPCHAT_COMMERCIAL_WARNINGS);
export const snapchatCommercialValidator = makeSnapchatValidator(SOCIAL_AD_TYPES.SNAPCHAT_COMMERCIAL, SNAPCHAT_COMMERCIAL_WARNINGS);

/**
 * Snapchat Story Carousel
*/

export const SNAPCHAT_CAROUSEL_DISCOVERY_CONSTRAINTS = {
	MAX_LENGTH_HEADLINE: 55,
	BACKGROUND_ACCEPT: '.png',
	BACKGROUND_MAX_BYTES: imageMaxBytes(2 * MEGA),
	BACKGROUND_DIMENSIONS: {
		width: 360,
		height: 600,
	},
	LOGO_ACCEPT: '.png',
	LOGO_MAX_BYTES: imageMaxBytes(2 * MEGA),
	LOGO_DIMENSTIONS: {
		width: 993,
		height: 284,
	},
};

const SNAPCHAT_CAROUSEL_DISCOVERY_VALIDATORS = {
	discoveryHeadline: [
		requiredNotNull,
		maxLength(SNAPCHAT_CAROUSEL_DISCOVERY_CONSTRAINTS.MAX_LENGTH_HEADLINE),
	],
	discoveryBackgroundImage: validateUnlessExternalRender([
		requiredAsset,
		filesize({
			max: SNAPCHAT_CAROUSEL_DISCOVERY_CONSTRAINTS.BACKGROUND_MAX_BYTES,
		}),
		exactDimensions(SNAPCHAT_CAROUSEL_DISCOVERY_CONSTRAINTS.BACKGROUND_DIMENSIONS),
	]),
	discoveryLogoImage: validateUnlessExternalRender([
		requiredAsset,
		filesize({
			max: SNAPCHAT_CAROUSEL_DISCOVERY_CONSTRAINTS.LOGO_MAX_BYTES,
		}),
		exactDimensions(SNAPCHAT_CAROUSEL_DISCOVERY_CONSTRAINTS.LOGO_DIMENSTIONS),
	]),
};

export const SNAPCHAT_CAROUSEL_CONSTRAINTS = {
	MAX_LENGTH_HEADLINE: 34,
	MAX_LENGTH_BRAND_NAME: 25,
	MAX_LENGTH_LANDING_PAGE: 800,
	IMAGE_ACCEPT: ".jpg, .jpeg, .png",
	MIN_VIDEO_SEC: 3,
	MAX_VIDEO_MIN: 3,
	IMAGE_MAX_BYTES: imageMaxBytes(5 * MEGA),
	IMAGE_DIMENSIONS: {
		width: 1080,
		height: 1920,
	},
	VIDEO_ACCEPT: ".mp4, .mov",
	VIDEO_MAX_BYTES: videoMaxBytes(1 * GIGA),
	VIDEO_DIMENSIONS: {
		width: 1080,
		height: 1920,
	},
};

export const SNAPCHAT_CAROUSEL_VALIDATORS = {
	headline: [
		requiredNotNull,
		maxLength(SNAPCHAT_CAROUSEL_CONSTRAINTS.MAX_LENGTH_HEADLINE),
	],
	brandName: [
		maxLength(SNAPCHAT_CAROUSEL_CONSTRAINTS.MAX_LENGTH_BRAND_NAME),
	],
	cardCount: [
		minCards(SOCIAL_AD_TYPES.SNAPCHAT_CAROUSEL),
	],
	disclaimerName: [
		conditionalRequiredNotNull("disclaimerEnabled"),
		maxLength(SNAPCHAT_COMMON_CONSTRAINTS.MAX_DISCLAIMER_NAME_LENGTH),
	],
	disclaimerText: [
		conditionalRequiredNotNull("disclaimerEnabled"),
		maxLength(SNAPCHAT_COMMON_CONSTRAINTS.MAX_DISCLAIMER_TEXT_LENGTH),
	],
};

const SNAPCHAT_CAROUSEL_STORY_VALIDATORS = makeDynamicConfig({
	image: validateUnlessExternalRender([
		requiredWithDynamicImageType,
		filesize({
			max: SNAPCHAT_CAROUSEL_CONSTRAINTS.IMAGE_MAX_BYTES,
		}),
		exactDimensions(SNAPCHAT_CAROUSEL_CONSTRAINTS.IMAGE_DIMENSIONS),
	]),
	video: validateUnlessExternalRender([
		requiredWithDynamicVideoType,
		filesize({
			max: SNAPCHAT_CAROUSEL_CONSTRAINTS.VIDEO_MAX_BYTES,
		}),
		duration({
			min: SNAPCHAT_CAROUSEL_CONSTRAINTS.MIN_VIDEO_SEC,
			max: SNAPCHAT_CAROUSEL_CONSTRAINTS.MAX_VIDEO_MIN * SECONDS_PER_MIN,
		}),
		exactDimensions(SNAPCHAT_CAROUSEL_CONSTRAINTS.VIDEO_DIMENSIONS),
	]),
	landingPage: [
		requiredNotNull,
		isURL,
		maxLength(SNAPCHAT_CAROUSEL_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE),
	],
	cta: [
		requiredNotNull,
	],
});

const SNAPCHAT_CAROUSEL_WARNINGS = mergeLists(
	{
		headline: [uniqueVariableName],
		brandName: [uniqueVariableName],
		discoveryHeadline: [uniqueVariableName],
		cards: [cardValidator(SNAPCHAT_CAROUSEL_STORY_VALIDATORS)],
	},
	makeDynamicConfig({ ...SNAPCHAT_CAROUSEL_VALIDATORS, ...SNAPCHAT_CAROUSEL_DISCOVERY_VALIDATORS })
);
export function snapchatCarouselWarning(allValues, props) {
	const formName = SOCIAL_AD_TYPES.SNAPCHAT_CAROUSEL;
	const values = allValues[formName] || {};

	if(!values.isDynamic){
		return errorMessageReducer(SNAPCHAT_CAROUSEL_WARNINGS, values, formName);
	}
}

export function snapchatCarouselValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.SNAPCHAT_CAROUSEL;
	const values = allValues[formName] || {};

	let validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	if(values.isDynamic){
		validators = {
			...validators,
			...SNAPCHAT_CAROUSEL_WARNINGS,
		};
	}

	return errorMessageReducer(validators, values, formName);
}
